import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  TextField
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  LocalFireDepartment as FireIcon,
  Factory as FactoryIcon,
  Engineering as EngineeringIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

const DailyProduction = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [filters, setFilters] = useState({
    furnace_code: '',
    ht_code: ''
  });

  const { enqueueSnackbar } = useSnackbar();
  const { api } = useAuth();

  // Fetch Daily Production Summary
  const { data: dailyData, isLoading, error, refetch } = useQuery(
    ['dailyProduction', selectedDate, filters],
    async () => {
      const params = new URLSearchParams({
        date: selectedDate,
        ...filters
      });

      const response = await api.get(`/heat-treatment/daily?${params}`);
      return response.data;
    },
    {
      onError: (error) => {
        enqueueSnackbar(`Error fetching daily production: ${error.message}`, { variant: 'error' });
      }
    }
  );

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading daily production data: {error.message}
        </Alert>
      </Box>
    );
  }

  const productionSummary = dailyData?.data?.summary || {};
  const furnaceWiseData = dailyData?.data?.furnaceWise || [];
  const htCodeWiseData = dailyData?.data?.htCodeWise || [];
  const detailedRecords = dailyData?.data?.records || [];

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Daily Heat Treatment Production
      </Typography>

      {/* Date and Filter Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Production Date"
              InputLabelProps={{ shrink: true }}
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Furnace Code"
              value={filters.furnace_code}
              onChange={(e) => setFilters({ ...filters, furnace_code: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="HT Code"
              value={filters.ht_code}
              onChange={(e) => setFilters({ ...filters, ht_code: e.target.value })}
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={() => refetch()}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PrintIcon />}
            >
              Print Report
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <FireIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h5" color="primary">
                    {productionSummary.totalBatches || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Batches
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <FactoryIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                  <Typography variant="h5" color="success.main">
                    {productionSummary.totalQuantity || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Quantity (Nos)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <EngineeringIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                  <Typography variant="h5" color="warning.main">
                    {productionSummary.totalTonnage || '0.000'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Tonnage (MT)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <AssessmentIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                  <Typography variant="h5" color="error.main">
                    {productionSummary.reworkBatches || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Rework Batches
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Furnace-wise Production */}
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">Furnace-wise Production</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Furnace Code</TableCell>
                    <TableCell>Batches</TableCell>
                    <TableCell>Quantity (Nos)</TableCell>
                    <TableCell>Tonnage (MT)</TableCell>
                    <TableCell>Rework</TableCell>
                    <TableCell>Efficiency (%)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {furnaceWiseData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No production data found for selected date
                      </TableCell>
                    </TableRow>
                  ) : (
                    furnaceWiseData.map((furnace, index) => (
                      <TableRow key={index}>
                        <TableCell>{furnace.furnace_code}</TableCell>
                        <TableCell>{furnace.batches}</TableCell>
                        <TableCell>{furnace.quantity}</TableCell>
                        <TableCell>{furnace.tonnage}</TableCell>
                        <TableCell>
                          {furnace.rework > 0 && (
                            <Chip label={furnace.rework} color="warning" size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`${furnace.efficiency || 0}%`} 
                            color={furnace.efficiency >= 90 ? 'success' : furnace.efficiency >= 70 ? 'warning' : 'error'} 
                            size="small" 
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* HT Code-wise Production */}
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">Heat Treatment Process-wise Production</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>HT Code</TableCell>
                    <TableCell>Process</TableCell>
                    <TableCell>Batches</TableCell>
                    <TableCell>Quantity (Nos)</TableCell>
                    <TableCell>Tonnage (MT)</TableCell>
                    <TableCell>Average Temp (°C)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {htCodeWiseData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No HT process data found for selected date
                      </TableCell>
                    </TableRow>
                  ) : (
                    htCodeWiseData.map((ht, index) => (
                      <TableRow key={index}>
                        <TableCell>{ht.ht_code}</TableCell>
                        <TableCell>{ht.ht_process}</TableCell>
                        <TableCell>{ht.batches}</TableCell>
                        <TableCell>{ht.quantity}</TableCell>
                        <TableCell>{ht.tonnage}</TableCell>
                        <TableCell>{ht.avg_temp}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Detailed Production Records */}
          <Paper>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">Detailed Production Records</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Die No</TableCell>
                    <TableCell>Heat Symbol</TableCell>
                    <TableCell>HT Code</TableCell>
                    <TableCell>Furnace</TableCell>
                    <TableCell>Qty1</TableCell>
                    <TableCell>Qty2</TableCell>
                    <TableCell>Qty3</TableCell>
                    <TableCell>Total Tonnage</TableCell>
                    <TableCell>Rework</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailedRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        No detailed production records found for selected date
                      </TableCell>
                    </TableRow>
                  ) : (
                    detailedRecords.map((record, index) => (
                      <TableRow key={index}>
                        <TableCell>{record.time || '--:--'}</TableCell>
                        <TableCell>{record.die_no}</TableCell>
                        <TableCell>{record.heat_symbol}</TableCell>
                        <TableCell>{record.ht_code}</TableCell>
                        <TableCell>{record.furnace_code}</TableCell>
                        <TableCell>{record.quantity1}</TableCell>
                        <TableCell>{record.quantity2}</TableCell>
                        <TableCell>{record.quantity3}</TableCell>
                        <TableCell>
                          {((record.tonnage1 || 0) + (record.tonnage2 || 0) + (record.tonnage3 || 0)).toFixed(3)}
                        </TableCell>
                        <TableCell>
                          {record.rework === 'Y' ? 
                            <Chip label="Yes" color="warning" size="small" /> : 
                            <Chip label="No" color="success" size="small" />
                          }
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default DailyProduction;